/**
 * Android yönetim uygulamasına (Faz 10) özgü düğme ve ekranların yardım metinleri.
 * Panelle ortak işlemler (sipariş durumu, ödeme alındı, stok hareketi…) panelin anahtarlarını kullanır;
 * burada yalnızca mobilde olan denetimler vardır. Uygulama bu dosyayı paketle birlikte taşır.
 */
import type { HelpContent } from './entries.tr';

const G = 'docs/ANDROID_BUILD_TR.md';

export const MOBILE_HELP_TR = {
  'mobile.page.home': {
    kind: 'page',
    title: 'Ana Sayfa (mobil)',
    short: 'Bugünkü siparişler ve ilgilenmeniz gereken işler.',
    whatItDoes:
      'Bugün gelen siparişlerin sayısı ve ödemesi alınmış tutarı, ödeme bekleyen ve hazırlanacak siparişler, yanıt bekleyen iade talepleri, kritik stok ve son kullanma tarihi yaklaşan lotlar gösterilir. Rakamlar sunucudaki gerçek kayıtlardan hesaplanır; veri yoksa tahmin gösterilmez.',
    whenToUse: 'Uygulamayı her açtığınızda.',
    dataChanged: 'Hiçbir veriyi değiştirmez.',
  },
  'mobile.page.more': {
    kind: 'page',
    title: 'Diğer',
    short: 'Müşteriler, iadeler, içerik onayı, SEO, kuponlar ve ayarlar.',
    whatItDoes:
      'Rolünüzün yetkisi olan ekranlar listelenir. Yetkiniz olmayan işlemler hiç gösterilmez; gösterilse bile sunucu yetki denetimini ayrıca yapar.',
  },
  'mobile.page.count': {
    kind: 'page',
    title: 'Barkodla sayım',
    short: 'Rafta saydığınız ürünleri kamerayla okutup depo stoğunu düzeltir.',
    whatItDoes:
      'Depoyu seçin ve ürünleri okutun; her okutma adedi bir artırır, adedi elle de değiştirebilirsiniz. Sayım cihazda saklanır, internet yokken de devam eder. "Sayımı gönder" her satır için sayılan adedi o depodaki yeni toplam olarak kaydeder; fark stok defterine "Sayım" hareketi olarak yazılır.',
    whenToUse: 'Dönem sonu sayımında, raf kontrolünde veya stok tutarsızlığı fark edince.',
    dataChanged: 'Gönderilen her satır için stok defterine bir "Sayım" hareketi eklenir.',
    requiredPermission: 'Stok değiştirme.',
    reversible:
      'Stok hareketleri silinmez. Yanlış sayım, doğru adetle yeniden sayım gönderilerek veya "Düzeltme" hareketiyle dengelenir.',
    commonErrors: [
      {
        problem: '"Lot seçin" uyarısı',
        solution:
          'Ürünün bu depoda lot/son kullanma tarihli stoğu var. Saydığınız paketlerin lot numarasını seçin; sayım lot bazında tutulur.',
      },
      {
        problem: 'Okutulan barkod bulunamadı',
        solution:
          'Ürüne barkod tanımlanmamış olabilir. Ürünü adıyla bulup ürün ekranından "Barkod ata" ile barkodu kaydedin.',
      },
    ],
    guideUrl: `${G}#barkodla-sayim`,
  },
  'mobile.page.review': {
    kind: 'page',
    title: 'Uzman onayı bekleyenler',
    short: 'Veteriner Uzman İnceleyicinin onayını bekleyen ürün, yazı, sayfa, banner ve sorular.',
    whatItDoes:
      'Sağlık içeriği olan veya otomatik denetimin riskli ifade bulduğu içerik, uzman onaylamadan vitrinde yayımlanmaz. Bulunan ifadeler alan adıyla gösterilir. Onay, içeriğin o anki hâline bağlıdır: içerik sonra değişirse yeniden onay gerekir.',
    whenToUse: 'Yeni ürün veya yazı eklendikten sonra, onay bildirimi geldiğinde.',
    requiredPermission: 'Ürün sağlık beyanı onayı veya içerik uzman incelemesi.',
  },
  'mobile.page.settings': {
    kind: 'page',
    title: 'Ayarlar',
    short: 'Hesap, işletme ve mağaza seçimi, biyometrik kilit, dil ve açık oturumlar.',
  },
  'mobile.server_url': {
    kind: 'field',
    title: 'Panel adresi',
    short: 'Bağlanılacak panelin adresi (ör. panel.zootopia.com.tr).',
    whatItDoes:
      'Uygulama yalnızca https ile bağlanır. Deneme ortamı için staging panel adresini yazabilirsiniz. Adres değiştirmek için önce çıkış yapın.',
    commonErrors: [
      {
        problem: '"Sunucuya ulaşılamadı"',
        solution: 'Adresin doğru olduğunu ve telefonun internete bağlı olduğunu kontrol edin.',
      },
    ],
  },
  'mobile.biometric': {
    kind: 'action',
    title: 'Biyometrik kilit',
    short: 'Uygulama açılırken parmak izi, yüz veya ekran kilidi sorulur.',
    whatItDoes:
      'Açıksa uygulama her açılışta ve 1 dakikadan uzun arka planda kaldıktan sonra kilitlenir. Doğrulama telefonun kendi güvenlik sistemiyle yapılır; parmak izi veya yüz verisi uygulamaya ya da sunucuya gelmez.',
    whenToUse: 'Telefonunuzu başkalarının da kullanabileceği durumlarda mutlaka açın.',
    dataChanged: 'Yalnızca bu cihazdaki tercihi değiştirir.',
    reversible: 'Evet, istediğiniz zaman kapatabilirsiniz.',
    commonErrors: [
      {
        problem: 'Seçenek açılmıyor',
        solution: 'Telefon ayarlarından parmak izi veya yüz tanıma ekleyin; ekran kilidi tanımlı olmalı.',
      },
    ],
  },
  'mobile.scan': {
    kind: 'action',
    title: 'Barkod okut',
    short: 'Kamerayla barkodu okutur; ürün veya takip numarası alanına yazar.',
    whatItDoes:
      'EAN-13, EAN-8, UPC, Code 128, Code 39, ITF ve karekod okunur. Görüntü cihazda işlenir, hiçbir yere gönderilmez.',
    commonErrors: [
      {
        problem: 'Kamera izni istendi',
        solution: 'Barkod okutmak için izin verin. Reddettiyseniz telefon ayarlarından izni açabilirsiniz.',
      },
    ],
  },
  'mobile.take_photo': {
    kind: 'action',
    title: 'Fotoğraf ekle',
    short: 'Ürün görselini kamerayla çekin veya galeriden seçin.',
    whatItDoes:
      'Görsel sunucuya yüklenir (en fazla 5 MB; PNG, JPEG, WebP) ve ürünün görsellerine en sona eklenir. İlk görsel vitrinde kapak olarak kullanılır; sıralamayı panelden değiştirebilirsiniz.',
    requiredPermission: 'Dosya yükleme ve ürün düzenleme.',
    reversible: 'Görsel panelden üründen kaldırılabilir.',
  },
  'mobile.barcode_assign': {
    kind: 'action',
    title: 'Barkod ata',
    short: 'Okuttuğunuz barkodu bu varyanta kaydeder.',
    whatItDoes:
      'Barkod (GTIN) 8-14 haneli olmalıdır. Kaydedildikten sonra ürün barkodla aranabilir ve sayımda okutulabilir. Aynı barkod başka bir varyantta kayıtlıysa sunucu kaydı reddeder.',
    requiredPermission: 'Ürün düzenleme ve maliyet görüntüleme (varyant kaydında maliyet korunur).',
    reversible: 'Evet, yeni barkod okutarak değiştirebilirsiniz.',
  },
  'mobile.count_lot': {
    kind: 'field',
    title: 'Lot',
    short: 'Saydığınız paketlerin lot numarası.',
    whatItDoes:
      'Ürünün bu depoda birden fazla lotu olabilir; sayım her lot için ayrı toplamdır. Paketin üzerindeki lot numarasını seçin.',
  },
  'mobile.count_submit': {
    kind: 'action',
    title: 'Sayımı gönder',
    short: 'Hazır satırları stok defterine "Sayım" olarak kaydeder.',
    whatItDoes:
      'Her satırdaki adet, o depo (ve lot) için yeni toplamdır; sunucu farkı hesaplayıp stok defterine yazar. Bağlantı kesilirse kalan satırları yeniden gönderebilirsiniz; aynı satır iki kez işlenmez.',
    dataChanged: 'Stok bakiyesi ve stok defteri.',
    requiredPermission: 'Stok değiştirme.',
    reversible: 'Hareket silinmez; doğru adetle yeniden sayım gönderilir.',
  },
  'mobile.count_discard': {
    kind: 'action',
    title: 'Sayımı sil',
    short: 'Cihazdaki sayımı siler; gönderilmiş satırlar stokta kalır.',
    reversible: 'Hayır. Gönderilmemiş satırlar kaybolur.',
  },
  'mobile.store_switch': {
    kind: 'field',
    title: 'Mağaza',
    short: 'Mağazaya bağlı ekranlar (içerik onayı, SEO, yayın) bu mağazayı kullanır.',
  },
  'mobile.organization_switch': {
    kind: 'action',
    title: 'İşletme değiştir',
    short: 'Birden fazla işletmeye üyeyseniz diğerine geçer.',
    whatItDoes:
      'Geçişte cihazdaki önbellek ve gönderilmemiş sayım silinir; ekranlar yeni işletmenin verisiyle açılır.',
  },
  'mobile.sign_out': {
    kind: 'action',
    title: 'Çıkış yap',
    short: 'Bu cihazdaki oturumu kapatır ve cihazdaki verileri siler.',
    whatItDoes:
      'Oturum sunucuda da kapatılır. Cihazdaki oturum anahtarı, önbellek ve gönderilmemiş sayım silinir.',
  },
  'mobile.notifications': {
    kind: 'page',
    title: 'Anlık bildirimler',
    short: 'Yeni sipariş, ödeme incelemesi, iade talebi, bayilik başvurusu ve iletişim mesajı bildirimleri.',
    whatItDoes:
      'Bu telefonun bildirim durumu, bildirim gelecek olaylar, sessiz saat ve bildirim alan cihazlarınız. Yalnızca yetkiniz olan olaylar bildirilir; bildirimde müşteri bilgisi yoktur. Bildirime dokununca ilgili ekran açılır.',
    guideUrl: `${G}#anlik-bildirimler`,
  },
  'mobile.notifications.device': {
    kind: 'field',
    title: 'Bu telefon',
    short: 'Bu telefonun bildirim izni ve kaydı.',
    whatItDoes:
      'Android 13 ve üstünde bildirim için izin gerekir. İzin verilince telefon sunucuya kaydolur; çıkış yapınca veya işletme değiştirince kayıt silinir.',
  },
  'mobile.notifications.allow': {
    kind: 'action',
    title: 'Bildirimleri aç',
    short: 'İzin ister ve bu telefonu bildirim alacak şekilde kaydeder.',
    whatItDoes:
      'Android izin penceresi açılır; izin verirseniz telefon sunucuya kaydedilir. Hangi olayların bildirileceğini aşağıdan seçersiniz.',
  },
  'mobile.notifications.open_settings': {
    kind: 'action',
    title: 'Telefon ayarlarını aç',
    short: 'İzin penceresi yeniden açılamıyorsa bildirimleri telefon ayarlarından açın.',
    whatItDoes:
      'Android, reddedilen izni bir süre sonra yeniden sormaz. Uygulamanın ayar sayfası açılır; Bildirimler bölümünden açıp uygulamaya dönün.',
  },
  'mobile.notifications.devices': {
    kind: 'field',
    title: 'Cihazlarım',
    short: 'Hesabınıza bildirim gelen telefonlar.',
    whatItDoes:
      'Her telefon uygulama açıldığında kendini kaydeder. Kullanmadığınız telefonu kaldırın; bildirim gönderilemeyen cihaz "Gönderilemiyor" görünür ve sunucu geçersiz kaydı kendiliğinden temizler.',
  },
  'mobile.notifications.remove': {
    kind: 'action',
    title: 'Cihazı kaldır',
    short: 'Bu cihaza bildirim gönderilmez.',
    whatItDoes:
      'Başka bir telefonu kaldırırsanız o telefon uygulama açılınca yeniden kaydolabilir. Bu telefonu kaldırırsanız siz "Bildirimleri aç" düğmesine basana kadar kaydolmaz.',
  },
  'mobile.offline': {
    kind: 'page',
    title: 'Çevrimdışı',
    short: 'İnternet yokken yalnızca cihazdaki sayıma devam edilebilir.',
    whatItDoes:
      'Sipariş, ürün ve müşteri verileri cihazda saklanmaz (kişisel veri korunur). Bağlantı gelince ekranlar kendiliğinden yenilenir; sayımı o zaman gönderebilirsiniz.',
  },
} satisfies Record<string, HelpContent>;

export type MobileHelpKey = keyof typeof MOBILE_HELP_TR;

type En = Pick<HelpContent, 'title' | 'short'> & Partial<Pick<HelpContent, 'whatItDoes'>>;

/** English titles and short descriptions; longer fields fall back to Turkish. */
export const MOBILE_HELP_EN: Record<MobileHelpKey, En> = {
  'mobile.page.home': {
    title: 'Home (mobile)',
    short: "Today's orders and the things that need your attention.",
  },
  'mobile.page.more': {
    title: 'More',
    short: 'Customers, returns, content review, SEO, coupons and settings.',
  },
  'mobile.page.count': {
    title: 'Barcode stock count',
    short: 'Scan what you count on the shelf and correct warehouse stock.',
  },
  'mobile.page.review': {
    title: 'Awaiting expert review',
    short: 'Products, posts, pages, banners and questions waiting for the veterinary reviewer.',
  },
  'mobile.page.settings': {
    title: 'Settings',
    short: 'Account, business and store, biometric lock, language and open sessions.',
  },
  'mobile.server_url': {
    title: 'Panel address',
    short: 'The panel to connect to (e.g. panel.zootopia.com.tr).',
  },
  'mobile.biometric': {
    title: 'Biometric lock',
    short: 'Asks for fingerprint, face or screen lock when the app opens.',
  },
  'mobile.scan': { title: 'Scan barcode', short: 'Reads a barcode with the camera.' },
  'mobile.take_photo': { title: 'Add photo', short: 'Take a product photo or pick one from the gallery.' },
  'mobile.barcode_assign': { title: 'Assign barcode', short: 'Saves the scanned barcode to this variant.' },
  'mobile.count_lot': { title: 'Lot', short: 'The lot number of the packages you counted.' },
  'mobile.count_submit': { title: 'Submit count', short: 'Records ready lines as stock counts.' },
  'mobile.count_discard': {
    title: 'Discard count',
    short: 'Deletes the count on this device; submitted lines stay recorded.',
  },
  'mobile.store_switch': { title: 'Store', short: 'Store-specific screens use this store.' },
  'mobile.organization_switch': {
    title: 'Switch business',
    short: 'Switch to another business you belong to.',
  },
  'mobile.sign_out': { title: 'Sign out', short: 'Ends the session and deletes data on this device.' },
  'mobile.notifications': {
    title: 'Push notifications',
    short: 'New order, payment review, return request, dealer application and contact message notifications.',
  },
  'mobile.notifications.device': {
    title: 'This phone',
    short: "This phone's notification permission and registration.",
  },
  'mobile.notifications.allow': {
    title: 'Turn on notifications',
    short: 'Asks for permission and registers this phone for notifications.',
  },
  'mobile.notifications.open_settings': {
    title: 'Open phone settings',
    short: 'If the permission cannot be asked again, turn notifications on in the phone settings.',
  },
  'mobile.notifications.devices': {
    title: 'My devices',
    short: 'Phones that receive notifications for your account.',
  },
  'mobile.notifications.remove': {
    title: 'Remove device',
    short: 'No notifications are sent to this device.',
  },
  'mobile.offline': { title: 'Offline', short: 'Without internet only the on-device count can continue.' },
};
